import { SortTypeEnum } from "@repo/models";
import { Column, GridBaseProps, GridPreLoaded, OriginalTimezoneDateColumn } from "@repo/ui";
import { useCallback, useMemo } from "react";

import { useDrugTestApi } from "../../../api/drugTestApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { useOnboardingContext } from "../../../contexts/onboardingContext";
import { useSegmentContext } from "../../../contexts/segmentContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import { DrugTest } from "../../../models";

interface Props extends GridBaseProps<DrugTest> {
	repId: number;
}

const COLUMNS = [
	new OriginalTimezoneDateColumn("Conducted On", "conductedOn").dateOnly().withSortBy(SortTypeEnum.descending),
	new Column("Requested By", "requestedByName"),
	new Column("Provider Name", "providerName"),
	new Column("Result", "resultSummary"),
] as Column<DrugTest>[];

export default function DrugTestGridBasic(props: Props) {
	const { repId } = useRepContext();
	const { useDrugTestQuery } = useDrugTestApi();
	const { data: drugTests, loaded, clearQuery } = useDrugTestQuery(repId);
	const [settings] = useSettingsQuery();
	const { drugTestSettings, multiStateEnabled } = settings;
	const expirationDays = drugTestSettings?.expirationDays;
	const { sessionId, session } = useOnboardingContext();
	const { segmentId } = useSegmentContext();
	const { getRepSegment, useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data: repSegments, loading: repSegmentsLoading } = useRepSegmentsQuery();

	const columns = useMemo(() => {
		const allColumns = [...COLUMNS];

		if (multiStateEnabled && !repSegmentsLoading)
			allColumns.push(
				new Column<DrugTest>("Onboarding State(s)", "onboardingSegmentsId").withFormat((ids) => {
					if (repSegments.length > 1 && repSegments.length === ids?.length) return "ALL";
					return ids
						.map(getRepSegment)
						.map((s) => s?.name)
						.filter((s) => s)
						.join(" | ");
				}),
			);

		if (expirationDays)
			allColumns.push(
				new Column<DrugTest>("Expired?", "expired").withFormat((e) =>
					e ? <span style={{ color: "red" }}>Expired</span> : "Active",
				),
			);

		return allColumns;
	}, [expirationDays, multiStateEnabled, repSegments, repSegmentsLoading]);

	const inSegment = useCallback(
		({ onboardingSegmentsId }: DrugTest) => {
			if (!segmentId) return true;
			return onboardingSegmentsId?.includes(segmentId);
		},
		[segmentId],
	);

	const inSessionOrActive = useCallback(
		({ onboardingSessionId, createdOn, conductedOn }: DrugTest) => {
			//if no session selected, show all
			if (!sessionId) return true;
			//if in session, show
			if (sessionId === onboardingSessionId) return true;

			//if not in session, but there is one selected, not included
			if (!expirationDays) return false;
			const dtDate = new Date(conductedOn ?? createdOn ?? new Date());
			const expirationDate = new Date(dtDate);
			expirationDate.setDate(expirationDate.getDate() + expirationDays);
			const sessionDate = new Date(session.createdOn);
			return sessionDate >= dtDate && sessionDate < expirationDate;
		},
		[session?.createdOn, sessionId, segmentId, expirationDays],
	);

	const filteredData = useMemo(() => {
		return (drugTests?.data ?? []).filter((b) => inSegment(b) && inSessionOrActive(b));
	}, [drugTests, inSessionOrActive, inSegment]);

	return (
		<GridPreLoaded
			{...props}
			title="Drug Tests"
			pageSize={3}
			columns={columns}
			values={filteredData}
			loading={!loaded}
			getData={clearQuery}
		/>
	);
}
